// ══════════════════════════════════════════════════════════════════════════════════════════════
// LE CENTRE DE NOTIFICATIONS — la cloche de la barre, et ce qu'elle ouvre.
//
// ⚠️ 🔴 LA PASTILLE NE MONTRE QU'UN COMPTE RÉEL, ET JAMAIS « 0 ». Le commentaire qui gardait cette
// cloche inerte l'écrivait déjà : « un 3 décoratif serait une donnée inventée, et c'est le pire endroit
// pour en inventer une — un compteur de notifications se lit d'un coup d'œil et se croit sur parole »
// (§23.0). Le compte vient du serveur, qui ne compte que les notifications OUVERTES et NON LUES.
//
// ⚠️ 🔴 UN CENTRE VIDE NE DIT PAS « TOUT VA BIEN » TANT QUE RIEN NE L'ALIMENTE. « Aucune alerte » et
// « personne ne surveille » s'affichent pareil, et seul le second est vrai aujourd'hui (§23.0) : d'où
// DEUX états vides distincts, et non un seul.
//
// ⚠️ 🔴 LE SERVEUR REND LE FAIT (`unfed`), L'ÉCRAN ÉCRIT LA PHRASE. Une phrase rendue par le serveur ne
// passe par AUCUN catalogue : elle s'afficherait en français à un compte anglais — ce que la première
// version faisait. Même geste que `whyCode` pour les doublons d’articles : le serveur conclut, l’écran
// nomme. Et le fait est « jamais rien reçu », pas « vide maintenant » : dérivé de la liste, il ferait
// ré-annoncer « personne ne surveille » à un producteur qui a tout résolu.
//
// ⚠️ 🔴 LE SERVEUR N’ENVOIE AUCUN TEXTE — il envoie un FAIT (`kind` + `params`), et `notifications-
// phrases.ts` le nomme. Un producteur qui composerait sa propre phrase la ferait rendre par le
// serveur, donc en français à un compte anglais, et aucune garde du web ne le verrait.
//
// ⚠️ LE BOUTON RESTE CELUI DE LA BARRE (`TopbarAction`) : écrire un second bouton pour l'occasion
// aurait posé une deuxième boîte qui aurait divergé au premier ajustement — c'est précisément ce que
// cette primitive a été extraite pour empêcher.
// ══════════════════════════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import { EmptyState, Icon, Popover, TopbarAction, showToast } from '@orgazone/ui-web';
import { api, type NotificationItem, type NotificationSweep, type NotificationsView } from './api';
import { phraseDuFait } from './notifications-phrases';
import { currentLang } from './lang';
import { t } from './i18n';

/** La teinte d'un niveau — les trois de RAP-5.5 (ℹ️ · ⚠️ · 🔴). */
const TEINTE: Record<NotificationItem['severity'], { fg: string; bg: string; icon: 'info' | 'alert' }> = {
  info: { fg: 'var(--ink-600)', bg: 'var(--ink-100)', icon: 'info' },
  warn: { fg: 'var(--amber-fg)', bg: 'var(--amber-bg)', icon: 'alert' },
  critical: { fg: 'var(--danger)', bg: 'var(--danger-bg)', icon: 'alert' },
};

/**
 * Un instant rendu dans la langue du compte.
 * ⚠️ C'est un INSTANT (le moment où le fait a été vu), pas une date fiscale : il se lit donc bien dans
 * le fuseau du lecteur.
 */
export function quandLisible(iso: string, lang: string = currentLang()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short' }).format(d);
}

/** La cloche, sa pastille et le panneau qu'elle ouvre. */
export function CentreNotifications() {
  const [ouvert, setOuvert] = useState(false);
  const [vue, setVue] = useState<NotificationsView | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(() => {
    api.notifications()
      .then((v) => { setVue(v); setErreur(null); })
      // ⚠️ LE MESSAGE RÉEL, et aucune vue de repli : une cloche qui affiche « 0 » parce que la requête a
      //    échoué dit exactement le contraire de ce qui se passe (§23.0).
      .catch((e: unknown) => { setVue(null); setErreur(e instanceof Error ? e.message : String(e)); });
  }, []);

  // ⚠️ AU MONTAGE, parce que la PASTILLE doit être juste sans qu'on ouvre le panneau — c'est sa seule
  //    raison d'être. Et à chaque ouverture, pour ne pas montrer un état vieux d'une heure.
  useEffect(charger, [charger]);

  const ouvrir = (o: boolean) => { setOuvert(o); if (o) charger(); };

  const marquerTout = () => {
    api.markAllNotificationsRead()
      .then(() => charger())
      .catch((e: unknown) => showToast(e instanceof Error ? e.message : String(e), 'err', 7000));
  };

  const marquerUne = (id: string) => {
    api.markNotificationsRead([id])
      .then(() => charger())
      .catch((e: unknown) => showToast(e instanceof Error ? e.message : String(e), 'err', 7000));
  };

  const nonLues = vue?.unread ?? 0;

  return (
    <Popover
      open={ouvert}
      onOpenChange={ouvrir}
      id="bo-notifications"
      label={t('Notifications')}
      geom={{ width: 380, align: 'end' }}
      trigger={(a) => (
        <TopbarAction
          icon="bell"
          label={t('Notifications')}
          onClick={a.onClick}
          trigger={{
            ref: a.ref,
            'aria-expanded': a['aria-expanded'],
            'aria-haspopup': a['aria-haspopup'],
            'aria-controls': a['aria-controls'],
          }}
          // ⚠️ PAS DE PASTILLE À ZÉRO : un « 0 » permanent apprend à ne plus regarder la cloche.
          badge={nonLues > 0 ? <Pastille n={nonLues} /> : undefined}
        />
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 460 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          padding: '12px 14px', borderBottom: '1px solid var(--ink-200)',
        }}>
          <strong style={{ fontSize: 'var(--t-title)', color: 'var(--ink-900)' }}>{t('Notifications')}</strong>
          {nonLues > 0 ? (
            <button
              type="button"
              onClick={marquerTout}
              style={{
                border: 'none', background: 'none', color: 'var(--teal-700)', cursor: 'pointer',
                fontSize: '12.5px', fontWeight: 600, padding: 0,
              }}
            >
              {t('Tout marquer comme lu')}
            </button>
          ) : null}
        </div>

        <div style={{ overflowY: 'auto', padding: erreur || !vue?.items.length ? 14 : 0 }}>
          {erreur ? (
            <p role="alert" style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-900)', whiteSpace: 'pre-wrap' }}>
              {erreur}
            </p>
          ) : !vue ? (
            <p style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture…')}</p>
          ) : vue.items.length === 0 ? (
            /**
             * ⚠️ 🔴 DEUX VIDES QUI NE DISENT PAS LA MÊME CHOSE, et c'est tout l'objet de ce bloc. Un
             * centre que PERSONNE n'alimente se lit « tout va bien » si on n'y prend pas garde ; un
             * centre alimenté et vide, lui, veut bien dire « rien à signaler ». Le serveur rend le FAIT
             * (`unfed`), l'écran écrit les deux phrases — dans la langue du compte.
             */
            vue.unfed ? (
              <EmptyState
                icon="bell"
                title={t('Personne ne surveille encore')}
                note={t('Aucune surveillance n’alimente ce centre pour l’instant : un centre vide ne veut donc pas dire « rien à signaler ». Les contrôles qui le rempliront restent à brancher.')}
                compact
              />
            ) : (
              <EmptyState
                icon="check"
                title={t('Rien à signaler')}
                note={<RienASignaler sweeps={vue.sweeps} />}
                compact
              />
            )
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {vue.items.map((it) => <Ligne key={it.id} item={it} onLire={() => marquerUne(it.id)} />)}
            </ul>
          )}
        </div>
      </div>
    </Popover>
  );
}

/**
 * CE QUE VAUT UN « RIEN À SIGNALER » — et à quelle heure il a été constaté.
 *
 * ⚠️ 🔴 UN SILENCE NE VAUT QUE PAR LA SURVEILLANCE DERRIÈRE. Le premier producteur livré n’avait rien à
 * signaler, et l’écran a continué d’annoncer « personne ne surveille » pendant tout ce temps : un
 * producteur qui ne trouve rien ne laissait aucune trace. Il signe désormais son passage, et l’écran
 * dit QUAND on a regardé.
 *
 * ⚠️ 🔴 ET IL AVOUE CE QU’IL N’A PAS PU VOIR : « rien à signaler » sur un magasin dont la base n’a pas
 * répondu est exactement le mensonge que §23.0 interdit — la phrase ne vaut que pour les magasins
 * réellement examinés.
 */
function RienASignaler({ sweeps }: { sweeps: NotificationSweep[] }) {
  const bloques = sweeps.reduce((n, s) => n + s.storesBlocked, 0);
  // ⚠️ LE PLUS RÉCENT DES PASSAGES : plusieurs producteurs tourneront à des cadences différentes, et
  //    c’est le dernier regard porté qui date le constat.
  const dernier = sweeps.map((s) => s.lastRunAt).sort().at(-1) ?? null;
  return (
    <>
      {dernier
        ? t('Aucune notification en attente pour vos magasins. Dernière vérification {v}.', { v: quandLisible(dernier) })
        : t('Aucune notification en attente pour vos magasins.')}
      {bloques > 0 ? (
        <div style={{ marginTop: 6, color: 'var(--amber-fg)' }}>
          {t('{n} magasin n’a pas pu être examiné : ce constat ne vaut pas pour lui.', { n: bloques })}
        </div>
      ) : null}
    </>
  );
}

/**
 * La pastille de compte.
 * ⚠️ AU-DELÀ DE 99 ELLE DIT « 99+ » : trois chiffres débordent de la boîte du contrôle, et un nombre
 * rogné se lit de travers — or c'est un nombre qu'on croit sur parole.
 */
function Pastille({ n }: { n: number }) {
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute', top: -4, right: -4, minWidth: 17, height: 17, padding: '0 4px',
        borderRadius: 999, background: 'var(--danger)', color: '#fff',
        fontSize: '10.5px', fontWeight: 800, lineHeight: '17px', textAlign: 'center',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {n > 99 ? '99+' : n}
    </span>
  );
}

/** Une notification dans la liste — son niveau, son titre, ce qu'elle dit, et depuis quand. */
function Ligne({ item, onLire }: { item: NotificationItem; onLire: () => void }) {
  const teinte = TEINTE[item.severity] ?? TEINTE.info;
  // ⚠️ LA PHRASE SE COMPOSE ICI, dans la langue du compte — le serveur n’en a envoyé aucune.
  const phrase = phraseDuFait(item);
  return (
    <li style={{
      display: 'flex', gap: 10, padding: '11px 14px', borderBottom: '1px solid var(--ink-100)',
      // ⚠️ LE NON-LU SE VOIT AU FOND, pas à une pastille de plus : la ligne entière change d'état.
      background: item.read ? 'transparent' : 'var(--ink-50)',
    }}>
      <span style={{
        flexShrink: 0, width: 22, height: 22, borderRadius: 'var(--r-sm)',
        background: teinte.bg, color: teinte.fg, display: 'grid', placeItems: 'center',
      }}>
        <Icon name={teinte.icon} size={13} stroke={2.2} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 'var(--t-body)', fontWeight: item.read ? 500 : 700, color: 'var(--ink-900)' }}>
          {phrase.title}
        </div>
        {phrase.body ? (
          <div style={{ fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.45, marginTop: 2 }}>{phrase.body}</div>
        ) : null}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
          <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>
            {quandLisible(item.lastSeenAt)}
          </span>
          {/* ⚠️ LE COMPTE D'OCCURRENCES NE S'AFFICHE QU'AU-DELÀ DE UN : « vu 1 fois » n'apprend rien,
              et « vu 47 fois » dit que le fait DURE — c'est toute l'information. */}
          {item.occurrences > 1 ? (
            <span style={{ fontSize: '11.5px', color: 'var(--ink-500)' }}>
              {t('vu {n} fois', { n: item.occurrences })}
            </span>
          ) : null}
          {!item.read ? (
            <button
              type="button"
              onClick={onLire}
              style={{
                border: 'none', background: 'none', color: 'var(--teal-700)', cursor: 'pointer',
                fontSize: '11.5px', fontWeight: 600, padding: 0, marginLeft: 'auto',
              }}
            >
              {t('Marquer comme lu')}
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );
}
